import React, { useState } from 'react';
import { ClockModel, ClockStationSegment } from 'shared/types';
import { formatTime, parseISODateFormat } from 'utils/date-utils';
import HoverTip from './HoverTip';
import { CLOCK_COLORS, CenterTotal, CenterSub } from './styles';

type Props = {
  model: ClockModel;
  size?: number;
};

type Tip = { x: number; y: number; label: string };

const TWO_PI = Math.PI * 2;

// Choose a tick interval giving roughly 6–10 marks around the dial.
const niceStep = (totalSec: number): number => {
  const steps = [15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200];
  return steps.find(s => totalSec / s <= 10) ?? totalSec / 8;
};

const SessionClock: React.FC<Props> = ({ model, size = 240 }): JSX.Element => {
  const [tip, setTip] = useState<Tip | null>(null);
  const show = (e: React.MouseEvent, label: string): void =>
    setTip({ x: e.clientX, y: e.clientY, label });
  const hide = (): void => setTip(null);

  // Wall-clock time of day for a given offset (seconds from session start).
  const tod = (sec: number): string =>
    parseISODateFormat(new Date(model.startMs + sec * 1000), 'h:mm:ssa');

  const cx = size / 2;
  const cy = size / 2;
  const rStationOut = size * 0.46;
  const rStationIn = size * 0.34;
  const rLeOut = size * 0.32;
  const rLeIn = size * 0.27;
  const rAnswer = rStationOut + size * 0.03;
  const rTick = rStationOut + size * 0.012;

  // 0 frac = top (12 o'clock), increasing clockwise.
  const point = (r: number, frac: number): [number, number] => {
    const ang = frac * TWO_PI - Math.PI / 2;
    return [cx + r * Math.cos(ang), cy + r * Math.sin(ang)];
  };

  const annular = (rIn: number, rOut: number, a: number, bIn: number): string => {
    let b = bIn;
    let span = b - a;
    if (span <= 0) return '';
    if (span > 0.9999) {
      b = a + 0.9999;
      span = 0.9999;
    }
    const large = span > 0.5 ? 1 : 0;
    const [x0o, y0o] = point(rOut, a);
    const [x1o, y1o] = point(rOut, b);
    const [x1i, y1i] = point(rIn, b);
    const [x0i, y0i] = point(rIn, a);
    return `M${x0o},${y0o} A${rOut},${rOut} 0 ${large} 1 ${x1o},${y1o} L${x1i},${y1i} A${rIn},${rIn} 0 ${large} 0 ${x0i},${y0i} Z`;
  };

  const stations = model.segments.filter(
    (s): s is ClockStationSegment => s.kind === 'station',
  );

  const activePct = model.totalSec > 0 ? Math.round((model.activeSec / model.totalSec) * 100) : 0;

  // Tick marks.
  const step = niceStep(model.totalSec);
  const ticks: number[] = [];
  for (let t = 0; t <= model.totalSec + 0.001; t += step) {
    ticks.push(t / model.totalSec);
  }

  return (
    <>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role='img'>
        {/* Background tracks (empty time reads as light = "nothing happening"). */}
        <circle
          cx={cx}
          cy={cy}
          r={(rStationIn + rStationOut) / 2}
          fill='none'
          stroke={CLOCK_COLORS.track}
          strokeWidth={rStationOut - rStationIn}
        />
        <circle
          cx={cx}
          cy={cy}
          r={(rLeIn + rLeOut) / 2}
          fill='none'
          stroke='#f7f7f7'
          strokeWidth={rLeOut - rLeIn}
        />

        {/* Tick marks. */}
        {ticks.map((frac, i) => {
          const [x0, y0] = point(rTick, frac);
          const [x1, y1] = point(rTick + size * 0.022, frac);
          return (
            <line key={`tick-${i}`} x1={x0} y1={y0} x2={x1} y2={y1} stroke='#d9d9d9' strokeWidth={1} />
          );
        })}

        {/* LE bands. */}
        {model.leBands.map((band, i) => (
          <path
            key={band.id}
            d={annular(rLeIn, rLeOut, band.startFrac, band.endFrac)}
            fill={CLOCK_COLORS.leBands[i % CLOCK_COLORS.leBands.length]}
            opacity={0.85}
            onMouseMove={(e): void =>
              show(
                e,
                `${band.leName ?? 'Learning experience'}\n${formatTime(band.durationSec)} in LE\n${tod(band.startSec)}`,
              )
            }
            onMouseLeave={hide}
          />
        ))}

        {/* Station arcs: idle base, then active overlay from the start of the block. */}
        {stations.map(seg => {
          const span = seg.endFrac - seg.startFrac;
          if (span <= 0) return null;
          const activeFrac = model.totalSec > 0 ? seg.activeSec / model.totalSec : 0;
          const activeEnd = Math.min(seg.endFrac, seg.startFrac + activeFrac);
          const station = `Station ${seg.stationNumber ?? 'Intro'}`;
          // The idle base path is only exposed where the green overlay isn't, so
          // hovering it reads as idle; the overlay reads as active.
          const idleLabel = seg.hasProgress
            ? `${station} · Idle\n${formatTime(seg.idleSec)} idle\n${tod(seg.startSec + seg.activeSec)}`
            : `${station} · Idle\nno active learning recorded\n${tod(seg.startSec)}`;
          const activeLabel = `${station} · Active learning\n${formatTime(seg.activeSec)} active\n${tod(seg.startSec)}`;
          return (
            <g key={seg.id}>
              <path
                d={annular(rStationIn, rStationOut, seg.startFrac, seg.endFrac)}
                fill={CLOCK_COLORS.idle}
                onMouseMove={(e): void => show(e, idleLabel)}
                onMouseLeave={hide}
              />
              {activeEnd > seg.startFrac && (
                <path
                  d={annular(rStationIn, rStationOut, seg.startFrac, activeEnd)}
                  fill={CLOCK_COLORS.active}
                  onMouseMove={(e): void => show(e, activeLabel)}
                  onMouseLeave={hide}
                />
              )}
            </g>
          );
        })}

        {/* Answer markers on the rim. */}
        {model.answers.map(ans => {
          const [x, y] = point(rAnswer, ans.frac);
          const verdict =
            ans.isCorrect === true ? 'correct' : ans.isCorrect === false ? 'incorrect' : 'unknown';
          const color =
            ans.isCorrect === true
              ? CLOCK_COLORS.correct
              : ans.isCorrect === false
                ? CLOCK_COLORS.incorrect
                : CLOCK_COLORS.unknownAnswer;
          return (
            <circle
              key={ans.id}
              cx={x}
              cy={y}
              r={size * 0.018}
              fill={color}
              stroke='#fff'
              strokeWidth={1}
              onMouseMove={(e): void =>
                show(e, `Answer · Station ${ans.stationNumber ?? '—'} · ${verdict}\n${tod(ans.sec)}`)
              }
              onMouseLeave={hide}
            />
          );
        })}

        {/* Center label. */}
        <foreignObject x={cx - rLeIn} y={cy - rLeIn} width={rLeIn * 2} height={rLeIn * 2}>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <CenterTotal>{formatTime(model.totalSec)}</CenterTotal>
            <CenterSub>{activePct}% active</CenterSub>
          </div>
        </foreignObject>
      </svg>
      {tip && (
        <HoverTip x={tip.x} y={tip.y}>
          {tip.label}
        </HoverTip>
      )}
    </>
  );
};

export default SessionClock;
